import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Stack,
  Chip,
  IconButton,
  Divider,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  CalendarToday as CalendarIcon,
  Search as SearchIcon,
  Category as CategoryIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';

const DocumentFilters = ({ open, filters, onClose, onApply, onClear }) => {
  const [localFilters, setLocalFilters] = useState(filters);

  // Document types
  const DOCUMENT_TYPES = [
    { value: 'prescription', label: 'Prescription' },
    { value: 'lab_result', label: 'Lab Result' },
    { value: 'consultation_note', label: 'Consultation Note' },
    { value: 'imaging', label: 'Imaging Report' },
    { value: 'medical_certificate', label: 'Medical Certificate' },
    { value: 'vaccination_record', label: 'Vaccination Record' },
    { value: 'insurance_card', label: 'Insurance Card' },
    { value: 'identity_proof', label: 'Identity Proof' },
    { value: 'other', label: 'Other' },
  ];

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setLocalFilters(prev => ({ ...prev, [field]: value }));
  };

  // Handle apply filters
  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  // Handle clear filters
  const handleClear = () => {
    const clearedFilters = {
      search: '',
      type: '',
      dateFrom: '',
      dateTo: '',
      verifiedOnly: false,
    };
    setLocalFilters(clearedFilters);
    onClear();
    onClose();
  };

  // Handle close
  const handleClose = () => {
    setLocalFilters(filters); // Reset to original filters
    onClose();
  };

  // Check if any filters are active
  const hasActiveFilters = Object.values(filters).some(value => 
    value !== '' && value !== false
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Document Filters
            </Typography>
          </Box>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Active Filters Display */}
          {hasActiveFilters && (
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Active Filters:</strong>
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                {filters.search && (
                  <Chip
                    icon={<SearchIcon />}
                    label={`Search: "${filters.search}"`}
                    size="small"
                    color="primary"
                  />
                )}
                {filters.type && (
                  <Chip
                    icon={<CategoryIcon />}
                    label={`Type: ${DOCUMENT_TYPES.find(t => t.value === filters.type)?.label}`}
                    size="small"
                    color="primary"
                  />
                )}
                {filters.dateFrom && (
                  <Chip
                    icon={<CalendarIcon />}
                    label={`From: ${filters.dateFrom}`}
                    size="small"
                    color="primary"
                  />
                )}
                {filters.dateTo && (
                  <Chip
                    icon={<CalendarIcon />}
                    label={`To: ${filters.dateTo}`}
                    size="small"
                    color="primary"
                  />
                )}
                {filters.verifiedOnly && (
                  <Chip
                    icon={<VerifiedIcon />}
                    label="Verified Only"
                    size="small"
                    color="primary"
                  />
                )}
              </Stack>
            </Alert>
          )}

          {/* Search Filter */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Search
            </Typography>
            <TextField
              label="Search documents"
              value={localFilters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              fullWidth
              placeholder="Search by title or description..."
              InputProps={{
                startAdornment: <SearchIcon color="action" />,
              }}
            />
          </Box>

          <Divider />

          {/* Document Type Filter */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Document Type
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Select Type</InputLabel>
              <Select
                value={localFilters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                label="Select Type"
              >
                <MenuItem value="">All Types</MenuItem>
                {DOCUMENT_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Divider />

          {/* Date Range Filters */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Date Range
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="From Date"
                type="date"
                value={localFilters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="To Date"
                type="date"
                value={localFilters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          </Box>

          <Divider />

          {/* Verification Filter */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Verification Status
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={localFilters.verifiedOnly}
                  onChange={(e) => handleFilterChange('verifiedOnly', e.target.checked)}
                />
              }
              label="Show only verified documents"
            />
          </Box>

          {/* Filter Tips */}
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Tip:</strong> You can combine multiple filters to narrow down your search results. 
              Leave fields empty to include all values.
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleClear}
          startIcon={<ClearIcon />}
          color="error"
          variant="outlined"
        >
          Clear All
        </Button>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          startIcon={<FilterIcon />}
        >
          Apply Filters
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentFilters; 